import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuShortcut,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
    ContextMenuTrigger,
} from "@/Components/ui/context-menu"
import { PlanBuilderContext, TaskGroupEdit } from "../Plan";
import { useContext, useState } from "react";
import { PageLockContext } from "@/Components/PageLockProvider";
import React from "react";
import { CharterIcon, CheckIcon, DeleteIcon, DeliverableIcon, DependenciesIcon, EditIcon, MilestoneIcon, PackageMilestoneIcon, StatusReportIcon, TaskIcon } from "@/Components/ui/Icons/TooltipIcon";
import { IndentIcon, OutdentIcon } from "lucide-react";

interface TaskContextMenuProps {
    children?: React.ReactNode;
}

export function TaskContextMenu({ children }: TaskContextMenuProps) {
    const lock = useContext(PageLockContext);
    const plan = useContext(PlanBuilderContext);

    const [allowCascade, setAllowCascade] = useState(true);
    const [allowSelected, setAllowSelected] = useState(true);
    const [allowDependents, setAllowDependents] = useState(true);
    const [indentDisabled, setIndentDisabled] = useState(true);
    const [outdentDisabled, setOutdentDisabled] = useState(true);

    const handleOpenChange = (isOpen: boolean) => {        
        if (!isOpen) return;
        
        // Safety check: if no task is selected, don't try to calculate
        if (!plan.selectedTaskID) {
            setAllowCascade(false);
            setAllowSelected(false);
            setAllowDependents(false);
            setIndentDisabled(true);
            setOutdentDisabled(true);
            return;
        }
        
        // Calculate dependency options when menu opens
        let cascade = true;
        let selected = true;
        let dependents = true;
        
        let previousTaskId = -1;
        let previousTaskDependents: number[] = [];

        plan.selectedTaskIDs.forEach((id) => {
            const taskDependents: number[] = [];
            plan.getDependentTaskIds(id, taskDependents);

            if (taskDependents.includes(previousTaskId) || previousTaskDependents.includes(id)) {
                cascade = false;
            }

            if (plan.selectedTaskID && taskDependents.includes(plan.selectedTaskID)) {
                dependents = false;
            }

            if (plan.selectedTaskID && id === plan.selectedTaskID
                && taskDependents.some(depId => plan.selectedTaskIDs.includes(depId) && depId !== id)) {
                selected = false;
            }

            previousTaskId = id;
            previousTaskDependents = [...taskDependents];
        });
        
        setAllowCascade(cascade);
        setAllowSelected(selected);
        setAllowDependents(dependents);
        
        // Calculate indent/outdent disabled when menu opens
        const taskId = plan.selectedTaskID;
        if (!taskId) {
            setIndentDisabled(true);
            setOutdentDisabled(true);
        } else {
            const task = plan.getTaskById(taskId);
            if (!task) {
                setIndentDisabled(true);
                setOutdentDisabled(true);
            } else {
                setOutdentDisabled(task.level === 0);
                setIndentDisabled(task.level !== undefined && task.level > 1);
            }
        }
    };

    function handleSetDependenciesClick(dependenciesType: "selected" | "dependents" | "cascade" | "remove") {
        const taskGroupEdit: TaskGroupEdit = {
                    highlightedTaskId: plan.selectedTaskID!, // can't be null, because this form is only shown when a task is selected
                    taskIDs: plan.selectedTaskIDs,
                    enableOwnerId: false,
                    enableWorkPackage: false,
                    enableIsDeliverable: false,
                    enableIsMilestone: false,
                    enableDisplayOnCharter: false,
                    enableDisplayOnStatusReport: false,
                    enableDuration: false,
                    enableSetDependencies: true,
                    enableSetSubtasks: false,
                    ownerId: "",
                    workstream: 0,
                    workPackage: 0,
                    isDeliverable: false,
                    isMilestone: false,
                    displayOnCharter: false,
                    displayOnStatusReport: false,
                    duration: 1,
                    setDependencies: dependenciesType,
                    setSubtasks: false,
                }
        plan.groupEditTasks(taskGroupEdit);
    }

    function handleSetTaskTypeClick(type: "task" | "deliverable" | "milestone" | "deliverableMilestone") {
        const taskGroupEdit: TaskGroupEdit = {
                    highlightedTaskId: plan.selectedTaskID!, // can't be null, because this form is only shown when a task is selected
                    taskIDs: plan.selectedTaskIDs,
                    enableOwnerId: false,
                    enableWorkPackage: false,
                    enableIsDeliverable: type === "deliverable" || type === "deliverableMilestone",
                    enableIsMilestone: type === "milestone" || type === "deliverableMilestone",
                    enableDisplayOnCharter: false,
                    enableDisplayOnStatusReport: false,
                    enableDuration: false,
                    enableSetDependencies: false,
                    enableSetSubtasks: false,
                    ownerId: "",
                    workstream: 0,
                    workPackage: 0,
                    isDeliverable: type === "deliverable" || type === "deliverableMilestone",
                    isMilestone: type === "milestone" || type === "deliverableMilestone",
                    displayOnCharter: false,
                    displayOnStatusReport: false,
                    duration: 1,
                    setDependencies: "remove",
                    setSubtasks: false,
                }
        plan.groupEditTasks(taskGroupEdit);
    }

    function handleSetFlagClick(flagType: "charter" | "statusReport") {
        const taskGroupEdit: TaskGroupEdit = {
                    highlightedTaskId: plan.selectedTaskID!, // can't be null, because this form is only shown when a task is selected
                    taskIDs: plan.selectedTaskIDs,
                    enableOwnerId: false,
                    enableWorkPackage: false,
                    enableIsDeliverable: false,
                    enableIsMilestone: false,
                    enableDisplayOnCharter: flagType === "charter",
                    enableDisplayOnStatusReport: flagType === "statusReport",
                    enableDuration: false,
                    enableSetDependencies: false,
                    enableSetSubtasks: false,
                    ownerId: "",
                    workstream: 0,
                    workPackage: 0,
                    isDeliverable: false,
                    isMilestone: false,
                    displayOnCharter: flagType === "charter",
                    displayOnStatusReport: flagType === "statusReport",
                    duration: 1,
                    setDependencies: "remove",
                    setSubtasks: false,
                }
        plan.groupEditTasks(taskGroupEdit);
    }

    function handleDeleteTask() {
        const tasks = plan.multiSelect ? plan.selectedTaskIDs : [plan.selectedTaskID];
        const tasksToDelete = tasks.filter((id): id is number => id !== null);
        
        // Use setTimeout to ensure the menu closes before deletion starts
        setTimeout(() => {
            plan.confirmDeleteTasks(tasksToDelete);
        }, 0);
    }

    function handleCompleteTask() {
        const taskIds = plan.selectedTaskIDs.length > 0 ? plan.selectedTaskIDs : plan.selectedTaskID ? [plan.selectedTaskID] : null;
        if (!taskIds) return;
        plan.completeMultipleTasks(taskIds);
    }

    return (
        <ContextMenu onOpenChange={handleOpenChange}>
            <ContextMenuTrigger>{children}</ContextMenuTrigger>
            <ContextMenuContent className="w-56">
                <ContextMenuItem onClick={() => handleCompleteTask()} disabled={!plan.selectedTaskID || lock.viewOnly}>
                    <CheckIcon />{plan.selectedTaskIDs.length > 1 ? "Complete Tasks" : "Complete Task"}
                    <ContextMenuShortcut>Alt + E</ContextMenuShortcut>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => plan.setShowEditForm(true)} disabled={!plan.selectedTaskID || lock.viewOnly}>
                    <EditIcon />{plan.selectedTaskIDs.length > 1 ? "Edit Tasks" : "Edit Task"}
                    <ContextMenuShortcut>Alt + E</ContextMenuShortcut>
                </ContextMenuItem>
                <ContextMenuItem disabled={!plan.selectedTaskID || lock.viewOnly} onClick={() => handleDeleteTask()}>
                    <DeleteIcon />{plan.selectedTaskIDs.length > 1 ? "Delete Tasks" : "Delete Task"}
                    <ContextMenuShortcut>Alt + D</ContextMenuShortcut>
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => plan.indentTasksUnderParent()} disabled={indentDisabled || lock.viewOnly}>
                    <IndentIcon />{plan.selectedTaskIDs.length > 1 ? "Indent Tasks" : "Indent Task"}
                    <ContextMenuShortcut>Alt + I</ContextMenuShortcut>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => plan.outdentTasksFromParent()} disabled={lock.viewOnly || outdentDisabled}>
                    <OutdentIcon />{plan.selectedTaskIDs.length > 1 ? "Outdent Tasks" : "Outdent Task"}
                    <ContextMenuShortcut>Alt + O</ContextMenuShortcut>
                </ContextMenuItem>
                {plan.multiSelect && !lock.viewOnly &&
                    (<>
                        <ContextMenuSeparator />
                        <ContextMenuSub>
                            <ContextMenuSubTrigger>
                                <DependenciesIcon className="mr-2" /> Set Dependencies
                            </ContextMenuSubTrigger>
                            <ContextMenuSubContent className="w-48">
                                {allowCascade && <ContextMenuItem onClick={() => handleSetDependenciesClick("cascade")}>Cascade dependencies</ContextMenuItem>}
                                {allowSelected && <ContextMenuItem onClick={() => handleSetDependenciesClick("selected")}>Selected are dependencies of highlighted Task</ContextMenuItem>}
                                {allowDependents && <ContextMenuItem onClick={() => handleSetDependenciesClick("dependents")}>Selected depend on highlighted Task</ContextMenuItem>}
                                <ContextMenuItem onClick={() => handleSetDependenciesClick("remove")}>Remove dependencies</ContextMenuItem>
                            </ContextMenuSubContent>
                        </ContextMenuSub>
                        <ContextMenuSub>
                            <ContextMenuSubTrigger>
                                <TaskIcon className="mr-2" /> Set Type
                            </ContextMenuSubTrigger>
                            <ContextMenuSubContent className="w-48">
                                <ContextMenuItem onClick={() => handleSetTaskTypeClick("task")}><TaskIcon />Task</ContextMenuItem>
                                <ContextMenuItem onClick={() => handleSetTaskTypeClick("deliverable")}><DeliverableIcon />Deliverable</ContextMenuItem>
                                <ContextMenuItem onClick={() => handleSetTaskTypeClick("milestone")}><MilestoneIcon />Milestone</ContextMenuItem>
                                <ContextMenuItem onClick={() => handleSetTaskTypeClick("deliverableMilestone")}><PackageMilestoneIcon />Deliverable Milestone</ContextMenuItem>
                            </ContextMenuSubContent>
                        </ContextMenuSub>
                        <ContextMenuSub>
                            <ContextMenuSubTrigger>
                                <CharterIcon  className="mr-2" /> Set Flags
                            </ContextMenuSubTrigger>
                            <ContextMenuSubContent className="w-48">
                                <ContextMenuItem onClick={() => handleSetFlagClick("statusReport")}><StatusReportIcon />Display on Status Report</ContextMenuItem>
                                <ContextMenuItem onClick={() => handleSetFlagClick("charter")}><CharterIcon />Display on Charter</ContextMenuItem>
                            </ContextMenuSubContent>
                        </ContextMenuSub>
                    </>)
                }
            </ContextMenuContent>
        </ContextMenu>
    )
}